import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { connect } from "react-redux";
import { multilanguage } from "redux-multilanguage";
import toast from "react-hot-toast";
import { wfetch } from "../../../utils/fetch";
import { getApiEndpoint } from "../../../utils/constants/api";
import { getCurrentAppBase } from "../../../utils/appBase";
import { composeLink } from "../../../utils/links";
import { decodeText } from "../../../utils/richText";

const MIN_CHARS = 3;
const DEBOUNCE_MS = 250;
const ROTATE_MS = 15000;
const LIMIT = 8;
const PROVIDER_EVENT = "shop-search-provider";

// Header search. Out of the box a page search: typing runs a full-text query (?q=) against
// the pages of the current shop and lists the hits; a click opens the page, Enter opens the
// first hit. The placeholder rotates through the authored example questions. Next to it the
// "Get Advice" button stands in for an AI product advisor.
//
// A project can plug in an AI search without touching this component: a script registers
// window.shopSearchProvider with search(query, context) and advice(context), optionally
// available(context) and init(context), and dispatches a "shop-search-provider" event. The
// context carries the shop's root page (with whatever fields the project added to its
// dialog, e.g. an API key), the site base and the language. With a provider that reports
// itself available, the box hands the query and the advice click over and keeps its own page
// suggestions out of the way. The sales demo wires ai12z this way through a Marketing Tag.
const readProvider = () => (typeof window !== "undefined" && window.shopSearchProvider) || null;

const SearchBox = ({ currentLanguageCode, strings, placeholder, questions = [], page }) => {
    const history = useHistory();
    const [term, setTerm] = useState("");
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [tick, setTick] = useState(0);
    const [provider, setProvider] = useState(readProvider);
    const wrapRef = useRef(null);

    const context = { page: page || {}, appBase: getCurrentAppBase(), language: currentLanguageCode };
    const external = !!provider && (typeof provider.available !== "function" || provider.available(context));

    useEffect(() => {
        const onProvider = () => setProvider(readProvider());
        window.addEventListener(PROVIDER_EVENT, onProvider);
        return () => window.removeEventListener(PROVIDER_EVENT, onProvider);
    }, []);

    useEffect(() => {
        if (external && typeof provider.init === "function") provider.init(context);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [external, provider, page]);

    useEffect(() => {
        if (questions.length < 2) return undefined;
        const timer = setInterval(() => setTick((t) => t + 1), ROTATE_MS);
        return () => clearInterval(timer);
    }, [questions.length]);

    useEffect(() => {
        const q = term.trim();
        if (external || q.length < MIN_CHARS) {
            setResults([]);
            return undefined;
        }

        let cancelled = false;
        const timer = setTimeout(() => {
            wfetch(
                getApiEndpoint("PAGE_SEARCH", {
                    queryParams: { "@ancestor": getCurrentAppBase(), q, limit: LIMIT, lang: currentLanguageCode },
                }),
            ).then((response) => {
                if (cancelled) return;
                const hits = response && response.data ? response.data.results || [] : [];
                setResults(hits.map((hit) => ({ path: hit["@path"], title: decodeText(hit.title) || hit["@name"] })));
                setOpen(true);
            });
        }, DEBOUNCE_MS);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [term, currentLanguageCode, external]);

    useEffect(() => {
        const closeOnOutsideClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", closeOnOutsideClick);
        return () => document.removeEventListener("mousedown", closeOnOutsideClick);
    }, []);

    const hint = questions.length ? questions[tick % questions.length] : placeholder || strings["search.placeholder"];
    const showSuggestions = !external && open && term.trim().length >= MIN_CHARS;

    const go = (path) => {
        setOpen(false);
        setTerm("");
        history.push(composeLink(path, currentLanguageCode));
    };

    const submit = async (e) => {
        e.preventDefault();
        const query = term.trim();
        if (!query) return;

        if (external) {
            const handled = await provider.search(query, context);
            if (handled !== false) {
                setTerm("");
                return;
            }
        }
        if (results[0]) {
            go(results[0].path);
            return;
        }
        toast(`Nothing found for "${query}"`);
    };

    const advice = async () => {
        if (external && typeof provider.advice === "function") {
            const handled = await provider.advice(context);
            if (handled !== false) return;
        }
        toast("Our product advisor is warming up. Ask us anything soon!");
    };

    return (
        <div ref={wrapRef} className="shop-header__searchbox">
            <form role="search" className="shop-header__search-form" onSubmit={submit}>
                <button type="submit" className="shop-header__search-submit" aria-label="Search">
                    <i className="pe-7s-search" />
                </button>
                <input
                    type="search"
                    className="shop-header__search-input"
                    placeholder={hint}
                    aria-label="Search"
                    autoComplete="off"
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    onFocus={() => results.length && setOpen(true)}
                />
                {/* type="button": inside the form it must not submit the search */}
                <button type="button" className="shop-header__advice" onClick={advice}>
                    <i className="pe-7s-magic-wand" /> <span>Get Advice</span>
                </button>
            </form>

            {showSuggestions && (
                <ul className="shop-header__suggestions">
                    {results.length ? (
                        results.map((hit) => (
                            <li key={hit.path}>
                                <button type="button" onClick={() => go(hit.path)}>
                                    <span className="shop-header__suggestion-title">{hit.title}</span>
                                    <span className="shop-header__suggestion-path">{hit.path.replace(getCurrentAppBase(), "") || "/"}</span>
                                </button>
                            </li>
                        ))
                    ) : (
                        <li className="shop-header__suggestion-empty">Nothing found for "{term.trim()}"</li>
                    )}
                </ul>
            )}
        </div>
    );
};

SearchBox.propTypes = {
    currentLanguageCode: PropTypes.string,
    page: PropTypes.object,
    placeholder: PropTypes.string,
    strings: PropTypes.object,
    questions: PropTypes.array,
};

export default connect()(multilanguage(SearchBox));